<template>
	<div class="draw-input-wrapper">
		<!-- ============ 主输入卡片（即梦风格：左参考图槽 + 右文本输入 + 底部 chips） ============ -->
		<div class="prompt-card">
			<div class="prompt-card__main">
				<!-- 左侧：参考图槽位 -->
				<!-- 模型不支持时整体包一层 tooltip，并把点击交给"打开模型弹窗" -->
				<el-tooltip
					v-if="!modelSupportsReference && !referenceImage"
					placement="top"
					popper-class="ref-slot-tooltip"
				>
					<div slot="content">
						<div style="font-weight:600;margin-bottom:4px">当前模型不支持参考图</div>
						<div style="font-size:12px;opacity:.85">点击切换到带「参考图」标签的模型即可上传图片</div>
					</div>
					<div
						class="ref-slot ref-slot--disabled"
						@click="modelDialogVisible = true"
					>
						<i class="el-icon-warning-outline ref-slot__placeholder"></i>
						<span class="ref-slot__badge">不支持</span>
					</div>
				</el-tooltip>

				<!-- 已上传参考图：直接展示 -->
				<div
					v-else-if="referenceImage"
					class="ref-slot ref-slot--has-image"
				>
					<img :src="referenceImage" class="ref-slot__img" alt="参考图" />
					<div class="ref-slot__mask">
						<el-upload
							class="ref-slot__replace"
							action=""
							:show-file-list="false"
							:before-upload="beforeRefUpload"
							:http-request="handleRefUpload"
							accept="image/png,image/jpeg,image/webp"
						>
							<i class="el-icon-refresh"></i>
						</el-upload>
						<span class="ref-slot__remove" @click="referenceImage = ''">
							<i class="el-icon-delete"></i>
						</span>
					</div>
				</div>

				<!-- 未上传 + 模型支持：可点击上传，hover 给提示，底部小角标"可选" -->
				<el-tooltip
					v-else
					placement="top"
					popper-class="ref-slot-tooltip"
				>
					<div slot="content">
						<div style="font-weight:600;margin-bottom:4px">参考图（可选）</div>
						<div style="font-size:12px;opacity:.85">上传一张图作为风格 / 内容参考；不传也能生成</div>
					</div>
					<el-upload
						class="ref-slot ref-slot--upload"
						action=""
						:show-file-list="false"
						:before-upload="beforeRefUpload"
						:http-request="handleRefUpload"
						accept="image/png,image/jpeg,image/webp"
					>
						<i class="el-icon-loading ref-slot__icon" v-if="refUploading"></i>
						<i class="el-icon-plus ref-slot__icon" v-else></i>
						<span class="ref-slot__badge ref-slot__badge--optional">参考图 · 选填</span>
					</el-upload>
				</el-tooltip>

				<!-- 右侧：提示词输入框 -->
				<textarea
					class="prompt-input"
					v-model="prompt"
					:placeholder="promptPlaceholder"
					@keydown.ctrl.enter="startDraw"
				></textarea>
			</div>

			<!-- 反向提示词（折叠展开） -->
			<transition name="slide-down">
				<div class="negative-area" v-if="showNegative">
					<i class="el-icon-minus-circle"></i>
					<input
						class="negative-input"
						v-model="negativePrompt"
						placeholder="反向提示词：描述不希望出现的内容，如 模糊、低质量、水印"
					/>
				</div>
			</transition>

			<!-- 卡片底部工具栏（药丸 chips + 圆形发送按钮） -->
			<div class="prompt-card__toolbar">
				<div class="toolbar-left">
					<!-- 模型 chip -->
					<div class="chip chip-primary" @click="modelDialogVisible = true">
						<i class="el-icon-magic-stick"></i>
						<span>{{ selectedModel ? selectedModel.modelName : '选择模型' }}</span>
						<i class="el-icon-arrow-down chip-arrow"></i>
					</div>

					<!-- 尺寸 chip -->
					<el-popover
						placement="top-start"
						trigger="click"
						popper-class="size-popover"
						v-model="sizePopVisible"
					>
						<div class="size-grid">
							<div
								v-for="(s, idx) in sizePresets"
								:key="idx"
								:class="['size-item', { active: sizeIndex === idx }]"
								@click="sizeIndex = idx; sizePopVisible = false"
							>
								<div class="size-ratio-box" :style="{ width: s.bw + 'px', height: s.bh + 'px' }"></div>
								<div class="size-ratio-label">{{ s.label }}</div>
								<div class="size-px">{{ s.width }}×{{ s.height }}</div>
							</div>
						</div>
						<div slot="reference" class="chip">
							<i class="el-icon-crop"></i>
							<span>{{ sizePresets[sizeIndex].label }} {{ resolutionLabel }}</span>
							<i class="el-icon-arrow-down chip-arrow"></i>
						</div>
					</el-popover>

					<!-- 反向词 chip（图标式，省占位） -->
					<div
						:class="['chip chip-icon', { 'chip-active': showNegative }]"
						@click="showNegative = !showNegative"
						title="反向提示词"
					>
						<span class="chip-tt"><sub>T</sub><sup>t</sup></span>
					</div>
				</div>

				<div class="toolbar-right">
					<span class="cost-badge" v-if="selectedModel">
						<i class="el-icon-magic-stick"></i>
						{{ selectedModel.usePrice }} / 张
					</span>
					<button
						class="send-btn"
						:class="{ loading: _status === '1', disabled: !canGenerate }"
						@click="startDraw"
						:disabled="_status === '1' || !canGenerate"
					>
						<i v-if="_status !== '1'" class="el-icon-top"></i>
						<i v-else class="el-icon-loading send-btn__spin"></i>
					</button>
				</div>
			</div>
		</div>

		<!-- 状态提示条 -->
		<transition name="fade">
			<div class="status-bar" v-if="_status === '1'">
				<span class="status-dots"><span></span><span></span><span></span></span>
				<span>图像生成中，通常需要 10~30 秒...</span>
			</div>
		</transition>

		<!-- 当前选中场景的小标记（极简） -->
		<div class="active-scene-tag" v-if="activeScene">
			<i class="el-icon-collection-tag"></i>
			<span>已应用「{{ activeScene.sceneName }}」场景</span>
			<span class="active-scene-tag__clear" @click="clearScene">清除</span>
		</div>

		<!-- ======== 模型选择弹窗 ======== -->
		<el-dialog
			:visible.sync="modelDialogVisible"
			width="860px"
			custom-class="draw-model-dialog"
			:append-to-body="true"
			:show-close="false"
		>
			<!-- 自定义头部 -->
			<div slot="title" class="dmd-header">
				<div class="dmd-header-inner">
					<div class="dmd-header-text">
						<span class="dmd-header-emoji">🎨</span>
						<div>
							<div class="dmd-title">选择绘画模型</div>
							<div class="dmd-subtitle">{{ enrichedModels.length }} 个模型可用，各有所长</div>
						</div>
					</div>
					<div class="dmd-search">
						<i class="el-icon-search"></i>
						<input v-model="modelKeyword" placeholder="搜索模型..." />
					</div>
					<i class="el-icon-close dmd-close" @click="modelDialogVisible = false"></i>
				</div>
			</div>

			<!-- 模型列表 -->
			<div class="dmd-body">
				<!-- 最近使用（无关键词时展示） -->
				<template v-if="recentModels.length > 0 && !modelKeyword">
					<div class="dmd-section-label">
						<i class="el-icon-time"></i> 最近使用
					</div>
					<div class="dmd-recent-row">
						<div
							v-for="m in recentModels"
							:key="'r-' + m.guid"
							:class="['dmd-recent-chip', { active: selectedModel && selectedModel.guid === m.guid }]"
							@click="selectModel(m)"
						>
							<span class="drc-dot" :style="{ background: m.iconBg }"></span>
							<span class="drc-name">{{ m.modelName }}</span>
							<i class="el-icon-check drc-check" v-if="selectedModel && selectedModel.guid === m.guid"></i>
							<i class="el-icon-close drc-remove" @click.stop="removeRecent(m.guid)"></i>
						</div>
					</div>
					<div class="dmd-section-label mt12">
						<i class="el-icon-set-up"></i> 全部模型
					</div>
				</template>

				<div class="dmd-grid">
					<div
						v-for="m in filteredModels"
						:key="m.guid"
						:class="['dmd-card', { 'dmd-card--active': selectedModel && selectedModel.guid === m.guid }]"
						@click="selectModel(m)"
					>
						<!-- 顶部彩色横幅 -->
						<div class="dmd-card-banner" :style="{ background: m.iconBg }">
							<i :class="m.icon" class="dmd-banner-icon"></i>
							<div class="dmd-card-badges">
								<span v-if="m.isReferenceImage === 1" class="dmd-badge dmd-badge--ref">参考图</span>
								<span v-if="m.isNew" class="dmd-badge dmd-badge--new">NEW</span>
								<span v-if="m.isHot" class="dmd-badge dmd-badge--hot">HOT</span>
							</div>
						</div>

						<!-- 内容区 -->
						<div class="dmd-card-body">
							<div class="dmd-card-name">{{ m.modelName }}</div>
							<div class="dmd-card-desc">{{ m.modelDesc || '通用 AI 图像生成模型' }}</div>
							<div class="dmd-card-tags">
								<span v-for="(tag, i) in m.tags" :key="i" class="dmd-tag">{{ tag }}</span>
							</div>
						</div>

						<!-- 底部：价格 + 选中标记 -->
						<div class="dmd-card-footer">
							<span class="dmd-price">
								<i class="el-icon-coin"></i>
								{{ m.usePrice }} 点/张
							</span>
							<transition name="dmd-check-fade">
								<span v-if="selectedModel && selectedModel.guid === m.guid" class="dmd-selected">
									<i class="el-icon-check"></i>
								</span>
							</transition>
						</div>
					</div>
				</div>

				<!-- 空状态 -->
				<div v-if="filteredModels.length === 0" class="dmd-empty">
					<i class="el-icon-picture-outline"></i>
					<span>没有找到匹配的模型</span>
				</div>
			</div>
		</el-dialog>
	</div>
</template>

<script>
import { getDrawImageModels } from '@/apis/draw';
import { uploadImgApi } from '@/apis/user';

const RECENT_KEY = 'dc_recent_draw_model_guids';
const MAX_RECENT  = 3;

const SIZE_PRESETS = [
	{ label: '1 : 1',  width: 1024, height: 1024, bw: 18, bh: 18 },
	{ label: '4 : 3',  width: 1365, height: 1024, bw: 18, bh: 14 },
	{ label: '3 : 4',  width: 1024, height: 1365, bw: 14, bh: 18 },
	{ label: '16 : 9', width: 1820, height: 1024, bw: 20, bh: 11 },
	{ label: '9 : 16', width: 1024, height: 1820, bw: 11, bh: 20 },
];

// 把 "1024x1024" 这种字符串映射回 sizePresets 的索引
function sizeStrToIndex(sizeStr) {
	if (!sizeStr) return 0;
	const idx = SIZE_PRESETS.findIndex(s => `${s.width}x${s.height}` === sizeStr);
	return idx >= 0 ? idx : 0;
}

export default {
	name: 'layout-draw-input',

	props: {
		_status:  { type: String,  default: '0' },
		_wsReady: { type: Boolean, default: false },
	},

	data() {
		return {
			// 模型相关
			orModels: [],
			selectedModel: null,
			recentModels: [],
			// 输入相关
			prompt: '',
			negativePrompt: '',
			showNegative: false,
			sizePresets: SIZE_PRESETS,
			sizeIndex: 0,
			sizePopVisible: false,
			modelDialogVisible: false,
			modelKeyword: '',
			// 场景关联（由父组件通过 ref 调用 applyScene/clearScene 注入）
			activeScene: null,         // 当前选中的场景对象
			// 参考图
			referenceImage: '',
			refUploading: false,
		};
	},

	computed: {
		// 所选模型是否支持参考图
		modelSupportsReference() {
			return !!this.selectedModel && Number(this.selectedModel.isReferenceImage) === 1;
		},

		// 输入框 placeholder
		promptPlaceholder() {
			if (this.activeScene) {
				return `已应用「${this.activeScene.sceneName}」场景的预设提示词，可继续编辑`;
			}
			if (this.modelSupportsReference) {
				return '描述你想生成的图片，左侧可上传参考图（非必填）作为风格 / 内容参考。';
			}
			return '描述你想生成的图片。';
		},

		// 分辨率提示标签（如 "高清 2K"）
		resolutionLabel() {
			const w = this.sizePresets[this.sizeIndex].width;
			if (w >= 1820) return '· 超清 2K';
			if (w >= 1024) return '· 高清';
			return '';
		},

		canGenerate() {
			return !!this.selectedModel && !!this.prompt.trim() && this._wsReady;
		},

		filteredModels() {
			const kw = (this.modelKeyword || '').trim().toLowerCase();
			if (!kw) return this.enrichedModels;
			return this.enrichedModels.filter(m =>
				(m.modelName || '').toLowerCase().includes(kw) ||
				(m.modelDesc || '').toLowerCase().includes(kw)
			);
		},

		enrichedModels() {
			// 为模型添加图标和标签（描述从数据库读取）
			return this.orModels.map(m => {
				let icon = 'el-icon-picture-outline';
				let iconBg = 'linear-gradient(135deg, #f0ebff, #e8e0ff)';
				let tags = ['AI绘画'];

				const name = (m.modelName || '').toLowerCase();
				if (name.includes('flux') || name.includes('pro')) {
					icon = 'el-icon-magic-stick';
					iconBg = 'linear-gradient(135deg, #e1f5fe, #b3e5fc)';
					tags = ['高质量', '专业级'];
				} else if (name.includes('xl') || name.includes('stable')) {
					icon = 'el-icon-camera-solid';
					iconBg = 'linear-gradient(135deg, #e8f4ff, #d1e9ff)';
					tags = ['写实', '通用'];
				} else if (name.includes('anime') || name.includes('cartoon')) {
					icon = 'el-icon-star-on';
					iconBg = 'linear-gradient(135deg, #fce4ec, #f8bbd0)';
					tags = ['动漫', '插画'];
				}

				// 支持参考图时，多加一个标签
				if (Number(m.isReferenceImage) === 1) {
					tags = [...tags, '图生图'];
				}

				return { ...m, icon, iconBg, tags };
			});
		},
	},

	async mounted() {
		this.orModels = await getDrawImageModels().catch(() => []);
		if (this.orModels.length === 0) return;

		// 优先恢复上次使用的模型，否则选第一个
		this._loadRecent();
		const lastGuid = (() => {
			try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]')[0]; } catch { return null; }
		})();
		const lastModel = lastGuid && this.orModels.find(m => m.guid === lastGuid);
		this.selectedModel = lastModel || this.orModels[0];
	},

	methods: {
		_loadRecent() {
			try {
				const guids = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
				this.recentModels = guids
					.map(g => this.enrichedModels.find(m => m.guid === g))
					.filter(Boolean)
					.slice(0, MAX_RECENT);
			} catch {
				this.recentModels = [];
			}
		},

		_saveRecent(model) {
			try {
				let guids = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
				guids = [model.guid, ...guids.filter(g => g !== model.guid)].slice(0, MAX_RECENT);
				localStorage.setItem(RECENT_KEY, JSON.stringify(guids));
				this.recentModels = guids
					.map(g => this.enrichedModels.find(m => m.guid === g))
					.filter(Boolean);
			} catch {}
		},

		removeRecent(guid) {
			try {
				let guids = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
				guids = guids.filter(g => g !== guid);
				localStorage.setItem(RECENT_KEY, JSON.stringify(guids));
				this.recentModels = this.recentModels.filter(m => m.guid !== guid);
			} catch {}
		},

		// ── 模型选择（用户主动切换；切换后清除场景标记，避免错配） ──
		selectModel(m) {
			this.selectedModel = m;
			this._saveRecent(m);
			this.modelDialogVisible = false;
			// 用户主动换模型 → 清除场景关联，但保留提示词
			if (this.activeScene) {
				this.activeScene = null;
				this.$emit('scene-cleared');
			}
			// 切到不支持参考图的模型时，自动清空已上传的参考图
			if (!this.modelSupportsReference) {
				this.referenceImage = '';
			}
		},

		// ── 场景：由父组件通过 ref 调用，自动联动模型/提示词/尺寸 ──
		applyScene(scene) {
			// 切换到该场景绑定的模型（若仍存在于模型列表）
			const targetModel = this.orModels.find(m => m.guid === scene.modelGuid);
			if (targetModel) {
				this.selectedModel = targetModel;
				this._saveRecent(targetModel);
			}

			// 自动填充提示词（覆盖现有内容；因为是用户主动点击场景）
			this.prompt = scene.promptTemplate || '';

			// 反向提示词联动
			if (scene.negativePrompt) {
				this.negativePrompt = scene.negativePrompt;
				this.showNegative   = true;
			}

			// 默认尺寸联动
			this.sizeIndex = sizeStrToIndex(scene.defaultSize);

			// 切到不支持参考图的模型时，清空已有参考图
			if (!this.modelSupportsReference) {
				this.referenceImage = '';
			}

			this.activeScene = scene;
		},

		// 由父组件 ref 调用 / 自身"清除"按钮调用
		clearScene() {
			this.activeScene = null;
			this.$emit('scene-cleared');
		},

		// ── 参考图上传 ──
		beforeRefUpload(file) {
			const allowed = ['image/png', 'image/jpeg', 'image/webp'];
			if (!allowed.includes(file.type)) {
				this.$message({ message: '仅支持 PNG / JPG / WebP 格式', type: 'warning' });
				return false;
			}
			if (file.size > 5 * 1024 * 1024) {
				this.$message({ message: '参考图大小不能超过 5MB', type: 'warning' });
				return false;
			}
			return true;
		},

		async handleRefUpload({ file }) {
			this.refUploading = true;
			try {
				const res = await uploadImgApi(file);
				// uploadImgApi 返回结构：{ code, data: 'https://xxx.cos...' } 或直接 string
				const url = typeof res === 'string'
					? res
					: (res?.data || res?.url || '');
				if (!url) {
					throw new Error('上传响应缺少图片地址');
				}
				this.referenceImage = url;
				this.$message({ message: '参考图上传成功', type: 'success' });
			} catch (e) {
				this.$message({ message: e?.message || '上传失败，请重试', type: 'error' });
			} finally {
				this.refUploading = false;
			}
		},

		// ── 发起绘画 ──
		startDraw() {
			if (!this.canGenerate || this._status === '1') return;

			const size = SIZE_PRESETS[this.sizeIndex];

			// 把参数上抛给父组件，由父组件通过 WebSocket 发送
			this.$emit('generate', {
				modelGuid:      this.selectedModel.guid,
				prompt:         this.prompt.trim(),
				negativePrompt: this.negativePrompt.trim(),
				width:          size.width,
				height:         size.height,
				// 参考图（仅当模型支持且已上传时传递）
				referenceImage: this.modelSupportsReference ? this.referenceImage : '',
				// 来源场景（追溯统计用）
				sceneGuid:      this.activeScene?.guid || '',
			});
		},
	},
};
</script>

<style lang="scss">
// ============================================================
// 即梦风格的输入卡片 + 场景大卡片 + 工具栏
// 设计要点：圆角大、留白多、阴影柔、主色蓝紫渐变
// ============================================================

// 设计 token
$ink-1:    #1a1a2e;     // 主标题字色
$ink-2:    #555;        // 次要字色
$ink-3:    #999;        // 辅助字色
$accent-1: #2c8df4;     // 主色蓝
$accent-2: #6a5cf6;     // 主色紫
$gradient: linear-gradient(135deg, $accent-1 0%, $accent-2 100%);
$bg-soft:  #f6f8fc;     // 卡片次级背景
$border-soft: #ecf0f6;

.draw-input-wrapper {
	width: 100%;
	margin: 0;
}

// ─── 主输入卡片 ───────────────────────────────────────
.prompt-card {
	background: #fff;
	border-radius: 20px;
	box-shadow: 0 6px 32px rgba(44, 141, 244, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04);
	border: 1px solid rgba(255, 255, 255, 0.8);
	overflow: hidden;
	transition: box-shadow 0.25s;

	&:focus-within {
		box-shadow: 0 8px 40px rgba(106, 92, 246, 0.13), 0 1px 3px rgba(0, 0, 0, 0.04);
	}

	// 主输入区：左参考图槽 + 右文本
	&__main {
		display: flex;
		gap: 18px;
		padding: 22px 24px 14px;
	}

	// 底部工具栏
	&__toolbar {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 8px 16px 14px;
		gap: 12px;
		flex-wrap: wrap;
	}
}

// 左侧参考图槽位
.ref-slot {
	flex-shrink: 0;
	width: 72px;
	height: 72px;
	border-radius: 12px;
	border: 1.5px dashed #d6dde8;
	background: $bg-soft;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	overflow: hidden;
	position: relative;
	transition: all 0.2s;
	color: #b0b8c5;

	&:hover:not(.ref-slot--disabled) {
		border-color: $accent-2;
		color: $accent-2;
		background: #f4f0ff;
	}

	// 模型不支持参考图：仍可点击（点击打开模型弹窗换模型），视觉上明确"不可用"
	&--disabled {
		cursor: pointer; // 不是禁用，是要引导用户切模型
		border-style: solid;
		border-color: #f0d4d4;
		background: #fff7f7;
		color: #e89090;

		&:hover {
			border-color: #f1b8b8;
			background: #fff0f0;
			color: #e07474;
		}
	}

	// 可上传状态（el-upload 包装版本）：tooltip + plus icon + 可选 badge
	&--upload {
		// 让 el-upload 内部的 trigger 撑满整个槽位
		::v-deep .el-upload {
			width: 100%;
			height: 100%;
			display: flex;
			align-items: center;
			justify-content: center;
		}
	}

	&__badge {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		font-size: 9px;
		text-align: center;
		padding: 2px 0;
		background: rgba(232, 144, 144, 0.18);
		color: #c66060;
		font-weight: 500;
		letter-spacing: 0.3px;
		line-height: 1.2;
		pointer-events: none;
	}

	// 可选小标签（蓝色调，对应支持参考图但未上传的状态）
	&__badge--optional {
		background: rgba(106, 92, 246, 0.12);
		color: $accent-2;
	}

	&__icon {
		font-size: 22px;
	}

	&__upload {
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;

		::v-deep .el-upload {
			width: 100%;
			height: 100%;
			display: flex;
			align-items: center;
			justify-content: center;
			border: none;
		}

		i { font-size: 22px; }
	}

	&__placeholder {
		font-size: 22px;
		margin-top: -8px; // 给 badge 留位置
	}

	&__img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	&__mask {
		position: absolute;
		inset: 0;
		background: rgba(0, 0, 0, 0.45);
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
		opacity: 0;
		transition: opacity 0.18s;
		color: #fff;
		font-size: 14px;

		.ref-slot__replace,
		.ref-slot__remove {
			cursor: pointer;
			padding: 4px;
			border-radius: 6px;
			display: inline-flex;

			&:hover { background: rgba(255, 255, 255, 0.18); }
			i { font-size: 14px; }
		}

		::v-deep .el-upload {
			color: #fff;
		}
	}

	&--has-image:hover &__mask { opacity: 1; }
}

// 右侧 prompt 输入框
.prompt-input {
	flex: 1;
	min-height: 72px;
	max-height: 200px;
	border: none;
	outline: none;
	resize: none;
	font-size: 15px;
	line-height: 1.7;
	color: $ink-1;
	background: transparent;
	font-family: inherit;
	padding-top: 4px;

	&::placeholder {
		color: #b0b8c5;
	}
}

// 反向提示词折叠区
.negative-area {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 0 24px 10px 24px;
	color: $ink-3;

	i { font-size: 14px; color: $accent-2; }

	.negative-input {
		flex: 1;
		border: none;
		outline: none;
		font-size: 13px;
		color: $ink-2;
		background: transparent;
		padding: 4px 0;

		&::placeholder { color: #c5cad3; }
	}
}

// ─── 工具栏药丸 chips ───────────────────────────
.toolbar-left {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.toolbar-right {
	display: flex;
	align-items: center;
	gap: 10px;
	margin-left: auto;
}

.chip {
	display: inline-flex;
	align-items: center;
	gap: 5px;
	height: 32px;
	padding: 0 12px;
	background: $bg-soft;
	border-radius: 16px;
	font-size: 13px;
	color: $ink-2;
	cursor: pointer;
	user-select: none;
	transition: all 0.15s;
	white-space: nowrap;
	border: 1px solid transparent;

	&:hover {
		background: #eef2f9;
		color: $accent-2;
	}

	i {
		font-size: 13px;
		opacity: 0.85;
	}

	.chip-arrow {
		font-size: 10px;
		opacity: 0.5;
		margin-left: 1px;
	}

	&-primary {
		background: linear-gradient(135deg, #ebf3ff, #f0ecff);
		color: $accent-2;
		font-weight: 500;

		&:hover { background: linear-gradient(135deg, #dde9ff, #e3dcff); }
	}

	&-icon {
		width: 32px;
		padding: 0;
		justify-content: center;
	}

	&.chip-active {
		background: #f0ecff;
		color: $accent-2;
		border-color: #d8cdff;
	}

	.chip-tt {
		display: inline-flex;
		align-items: baseline;
		font-family: serif;
		font-size: 14px;
		line-height: 1;

		sub { font-size: 12px; vertical-align: -2px; }
		sup { font-size: 9px; vertical-align: 4px; }
	}
}

.cost-badge {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	color: #ff9500;
	font-size: 12px;
	font-weight: 500;

	i { font-size: 12px; }
}

// 圆形发送按钮
.send-btn {
	width: 36px;
	height: 36px;
	border-radius: 50%;
	border: none;
	background: $gradient;
	color: #fff;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.2s;
	box-shadow: 0 4px 14px rgba(106, 92, 246, 0.35);

	i { font-size: 17px; }

	&:hover:not(.disabled):not(.loading) {
		transform: scale(1.06);
		box-shadow: 0 6px 18px rgba(106, 92, 246, 0.45);
	}

	&.disabled {
		background: #d8dde6;
		box-shadow: none;
		cursor: not-allowed;
	}

	&.loading {
		background: linear-gradient(135deg, #92a8e8, #b3a8ec);
		cursor: not-allowed;
	}

	&__spin { animation: spin 1s linear infinite; }
}

// ─── 状态条 ───────────────────────────────────────
.status-bar {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px 18px;
	margin-top: 12px;
	background: rgba(255, 255, 255, 0.7);
	backdrop-filter: blur(8px);
	border-radius: 12px;
	font-size: 13px;
	color: $ink-2;
	border: 1px solid rgba(106, 92, 246, 0.12);

	.status-dots {
		display: inline-flex;
		gap: 4px;

		span {
			width: 6px;
			height: 6px;
			border-radius: 50%;
			background: $accent-2;
			animation: bounce-dot 1.2s infinite ease-in-out;

			&:nth-child(2) { animation-delay: 0.2s; }
			&:nth-child(3) { animation-delay: 0.4s; }
		}
	}
}


// 当前应用场景小标签（输入卡片下方提示）
.active-scene-tag {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	margin-top: 14px;
	padding: 6px 12px 6px 10px;
	background: linear-gradient(135deg, rgba(44, 141, 244, 0.08), rgba(106, 92, 246, 0.1));
	border-radius: 16px;
	font-size: 12px;
	color: $accent-2;

	i { font-size: 13px; }

	&__clear {
		margin-left: 4px;
		cursor: pointer;
		opacity: 0.65;
		padding: 0 6px;
		border-radius: 8px;
		transition: all 0.15s;

		&:hover {
			opacity: 1;
			background: rgba(255, 255, 255, 0.6);
		}
	}
}

// ─── 折叠 / 渐变 动画 ───────────────────────────
.slide-down-enter-active,
.slide-down-leave-active {
	transition: all 0.2s ease;
	max-height: 80px;
	overflow: hidden;
}
.slide-down-enter,
.slide-down-leave-to {
	max-height: 0;
	opacity: 0;
}
.fade-enter-active, .fade-leave-active { transition: all 0.18s; }
.fade-enter, .fade-leave-to { opacity: 0; transform: translateY(-4px); }

// ── 绘画模型选择弹窗（高级版） ──────────────────────────────────
.draw-model-dialog {
	border-radius: 20px !important;
	overflow: hidden !important;

	.el-dialog__header {
		padding: 0 !important;
		background: linear-gradient(135deg, #1a1a2e 0%, #16213e 55%, #0f3460 100%);
	}

	.el-dialog__body {
		padding: 0 !important;
		background: #f5f6fa;
	}

	// 弹窗头部
	.dmd-header-inner {
		display: flex;
		align-items: center;
		gap: 16px;
		padding: 22px 28px;
	}

	.dmd-header-text {
		display: flex;
		align-items: center;
		gap: 14px;
		flex: 1;

		.dmd-header-emoji {
			font-size: 34px;
			line-height: 1;
			filter: drop-shadow(0 2px 8px rgba(255, 255, 255, 0.25));
		}

		.dmd-title {
			font-size: 18px;
			font-weight: 700;
			color: #fff;
			line-height: 1.2;
		}

		.dmd-subtitle {
			font-size: 12px;
			color: rgba(255, 255, 255, 0.5);
			margin-top: 3px;
		}
	}

	.dmd-search {
		display: flex;
		align-items: center;
		gap: 8px;
		background: rgba(255, 255, 255, 0.1);
		border: 1px solid rgba(255, 255, 255, 0.18);
		border-radius: 10px;
		padding: 7px 14px;
		width: 210px;
		transition: border-color 0.2s;

		&:focus-within {
			border-color: rgba(255, 255, 255, 0.4);
			background: rgba(255, 255, 255, 0.14);
		}

		i {
			color: rgba(255, 255, 255, 0.45);
			font-size: 13px;
			flex-shrink: 0;
		}

		input {
			background: none;
			border: none;
			outline: none;
			color: #fff;
			font-size: 13px;
			width: 100%;

			&::placeholder { color: rgba(255, 255, 255, 0.35); }
		}
	}

	.dmd-close {
		color: rgba(255, 255, 255, 0.45);
		font-size: 18px;
		cursor: pointer;
		flex-shrink: 0;
		padding: 5px;
		border-radius: 8px;
		transition: all 0.15s;

		&:hover {
			color: #fff;
			background: rgba(255, 255, 255, 0.12);
		}
	}

	// 列表区域
	.dmd-body {
		padding: 20px 22px 24px;
		max-height: 500px;
		overflow-y: auto;

		&::-webkit-scrollbar { width: 5px; }
		&::-webkit-scrollbar-thumb { background: #d5d5e0; border-radius: 3px; }
	}

	// 分区标题
	.dmd-section-label {
		font-size: 12px;
		font-weight: 600;
		color: #999;
		letter-spacing: 0.4px;
		text-transform: uppercase;
		margin-bottom: 10px;
		display: flex;
		align-items: center;
		gap: 5px;

		&.mt12 { margin-top: 18px; }
	}

	// 最近使用 chips 行
	.dmd-recent-row {
		display: flex;
		gap: 8px;
		flex-wrap: wrap;
		margin-bottom: 4px;
	}

	.dmd-recent-chip {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 6px 10px 6px 8px;
		background: #fff;
		border: 1.5px solid #e8e8f0;
		border-radius: 20px;
		cursor: pointer;
		font-size: 12px;
		color: #444;
		transition: all 0.15s;
		position: relative;

		&:hover {
			border-color: #c4a8f8;
			background: #faf7ff;
			color: #6126f5;

			.drc-remove { opacity: 1; }
		}

		&.active {
			border-color: #6126f5;
			background: #f0ebff;
			color: #6126f5;

			.drc-check { display: flex; }
			.drc-remove { display: none; }
		}

		.drc-dot {
			width: 8px;
			height: 8px;
			border-radius: 50%;
			flex-shrink: 0;
		}

		.drc-name {
			max-width: 120px;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.drc-check {
			display: none;
			width: 14px;
			height: 14px;
			border-radius: 50%;
			background: #6126f5;
			color: #fff;
			font-size: 9px;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
		}

		.drc-remove {
			color: #bbb;
			font-size: 11px;
			opacity: 0;
			transition: opacity 0.15s;
			flex-shrink: 0;

			&:hover { color: #f56c6c; }
		}
	}

	.dmd-grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 14px;
	}

	// 模型卡片
	.dmd-card {
		background: #fff;
		border-radius: 14px;
		border: 2px solid transparent;
		overflow: hidden;
		cursor: pointer;
		transition: all 0.22s ease;
		box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);

		&:hover {
			transform: translateY(-3px);
			box-shadow: 0 10px 28px rgba(97, 38, 245, 0.13);
			border-color: #c4a8f8;
		}

		&.dmd-card--active {
			border-color: #6126f5;
			box-shadow: 0 4px 20px rgba(97, 38, 245, 0.2);

			.dmd-card-name { color: #6126f5; }
			.dmd-card-banner { opacity: 1; filter: brightness(1.05); }
		}
	}

	// 顶部彩色横幅
	.dmd-card-banner {
		height: 68px;
		display: flex;
		align-items: center;
		justify-content: center;
		position: relative;
		opacity: 0.92;
		transition: opacity 0.2s;

		.dmd-banner-icon {
			font-size: 30px;
			color: rgba(97, 38, 245, 0.65);
			filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.1));
		}

		.dmd-card-badges {
			position: absolute;
			top: 8px;
			right: 8px;
			display: flex;
			gap: 4px;
		}
	}

	.dmd-badge {
		font-size: 9px;
		font-weight: 700;
		padding: 2px 6px;
		border-radius: 8px;
		letter-spacing: 0.3px;

		&.dmd-badge--new {
			background: linear-gradient(135deg, #6126f5, #9b59f5);
			color: #fff;
		}

		&.dmd-badge--hot {
			background: linear-gradient(135deg, #ff6b6b, #ff8e8e);
			color: #fff;
		}

		&.dmd-badge--ref {
			background: linear-gradient(135deg, #00b894, #55efc4);
			color: #fff;
		}
	}

	// 内容区
	.dmd-card-body {
		padding: 12px 14px 10px;

		.dmd-card-name {
			font-size: 13px;
			font-weight: 600;
			color: #1a1a2e;
			margin-bottom: 6px;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			transition: color 0.18s;
		}

		.dmd-card-desc {
			font-size: 11px;
			color: #888;
			line-height: 1.55;
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
			overflow: hidden;
			min-height: 34px;
			margin-bottom: 9px;
		}

		.dmd-card-tags {
			display: flex;
			gap: 5px;
			flex-wrap: wrap;
		}

		.dmd-tag {
			font-size: 10px;
			color: #6126f5;
			background: #f0ebff;
			padding: 2px 8px;
			border-radius: 10px;
			font-weight: 500;
		}
	}

	// 底部
	.dmd-card-footer {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 8px 14px 12px;
		border-top: 1px solid #f3f3f6;

		.dmd-price {
			font-size: 12px;
			color: #666;
			font-weight: 500;

			i { color: #f5a623; margin-right: 3px; }
		}

		.dmd-selected {
			width: 20px;
			height: 20px;
			border-radius: 50%;
			background: linear-gradient(135deg, #6126f5, #9b59f5);
			color: #fff;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 11px;
			box-shadow: 0 2px 6px rgba(97, 38, 245, 0.35);
		}
	}

	// 空状态
	.dmd-empty {
		text-align: center;
		padding: 64px 0;
		color: #ccc;

		i { font-size: 44px; display: block; margin-bottom: 12px; }
		span { font-size: 14px; }
	}
}

// 选中动画
.dmd-check-fade-enter-active,
.dmd-check-fade-leave-active { transition: all 0.18s ease; }
.dmd-check-fade-enter,
.dmd-check-fade-leave-to { opacity: 0; transform: scale(0.4); }

.size-popover {
	padding: 12px !important;
	border-radius: 12px !important;
	box-shadow: 0 8px 32px rgba(0,0,0,0.12) !important;

	.size-grid {
		display: flex;
		gap: 8px;

		.size-item {
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: 4px;
			width: 56px;
			padding: 8px 4px;
			border-radius: 8px;
			border: 1px solid #eee;
			cursor: pointer;
			transition: all 0.15s;

			&:hover { border-color: #c4a8f8; background: #faf7ff; }

			&.active {
				border-color: #6126f5;
				background: #f0ebff;
			}

			.size-ratio-box {
				background: #d0bfff;
				border-radius: 2px;
				min-width: 8px;
				min-height: 8px;
			}

			.size-ratio-label {
				font-size: 10px;
				font-weight: 600;
				color: #333;
				text-align: center;
			}

			.size-px {
				font-size: 9px;
				color: #aaa;
				text-align: center;
				white-space: nowrap;
			}
		}
	}
}

@keyframes bounce-dot {
	0%, 80%, 100% { transform: scale(0.7); opacity: 0.5; }
	40% { transform: scale(1); opacity: 1; }
}

@keyframes spin {
	to { transform: rotate(360deg); }
}
</style>
